import { FileTextOutlined } from '@ant-design/icons'
import { Tag, Tooltip } from 'antd'
import { DocumentDataContext } from 'features/libra/document/DocumentDataContext'
import { LibraDocumentContext } from 'features/libra/document/LibraDocumentContext'
import {
  ApmCitation,
  DocumentCitation,
  FieldValue,
  LlmCitation,
} from 'features/libra/ontology/ontologyFieldTypes'
import startCase from 'lodash/startCase'
import type { FC } from 'react'
import { useContext } from 'react'
import styled from 'styled-components'

/**
 * Safely reads the citation from a field value's llmValue envelope.
 *
 * gong-agent-service wraps llmValue as `{ value, citation }`. Older records
 * or test fixtures may have llmValue as a plain scalar, so this helper handles
 * both shapes gracefully.
 */
export function extractLlmCitation(
  fieldValue: FieldValue | undefined
): LlmCitation | null {
  if (!fieldValue?.llmValue) return null
  const lv = fieldValue.llmValue
  if (typeof lv === 'object' && lv !== null && 'citation' in (lv as object)) {
    return (
      (lv as { value: unknown; citation: LlmCitation | null }).citation ?? null
    )
  }
  return null
}

function isDocumentCitation(c: LlmCitation): c is DocumentCitation {
  // Some AI responses omit `doc`; treating those as filenames crashes shortDocName.
  return (
    c !== null &&
    typeof (c as DocumentCitation).doc === 'string' &&
    (c as DocumentCitation).doc !== 'APM component data'
  )
}

function isApmCitation(c: LlmCitation): c is ApmCitation {
  return c !== null && (c as ApmCitation).doc === 'APM component data'
}

function shortDocName(doc: string): string {
  const base = doc.split('/').pop() ?? doc
  return base.length > 30 ? base.slice(0, 27) + '...' : base
}

const CitationTag = styled(Tag)`
  cursor: default;
  font-size: 11px;
  margin: 0;
`

/**
 * Matches a citation's `doc` (filename from the LLM) against the list of
 * documents in the Libra viewer. Tries exact match first, then suffix match
 * for paths where the LLM only sees the base filename.
 */
function findEntryId(
  docName: string,
  documentsByCategory: ReadonlyMap<
    string,
    ReadonlyArray<{
      entry_id: string
      filename?: string
      visible_name?: string
    }>
  >
): string | null {
  const needle = docName.toLowerCase()
  for (const docs of documentsByCategory.values()) {
    for (const doc of docs) {
      const candidates = [doc.filename, doc.visible_name].filter(
        Boolean
      ) as string[]
      for (const candidate of candidates) {
        const c = candidate.toLowerCase()
        if (
          c === needle ||
          c.endsWith('/' + needle) ||
          c.endsWith('\\' + needle)
        ) {
          return doc.entry_id
        }
      }
    }
  }
  return null
}

export const AiCitationChip: FC<{ fieldValue: FieldValue | undefined }> = ({
  fieldValue,
}) => {
  // Direct context access lets citations render safely outside these providers.
  const libraDoc = useContext(LibraDocumentContext)
  const docData = useContext(DocumentDataContext)

  if (!fieldValue) return null

  const valueIsEmpty =
    fieldValue.value === null ||
    fieldValue.value === undefined ||
    (Array.isArray(fieldValue.value) && fieldValue.value.length === 0)
  if (valueIsEmpty) return null

  const citation = extractLlmCitation(fieldValue)

  if (citation !== null) {
    if (isDocumentCitation(citation)) {
      const label = [
        shortDocName(citation.doc),
        citation.page !== null ? `p.${citation.page}` : null,
      ]
        .filter(Boolean)
        .join(' - ')

      const tooltipContent = (
        <div style={{ maxWidth: 320 }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            {citation.doc}
            {citation.page !== null ? `, page ${citation.page}` : ''}
          </div>
          {citation.excerpt && (
            <div style={{ fontStyle: 'italic' }}>
              &quot;{citation.excerpt}&quot;
            </div>
          )}
        </div>
      )

      const entryId =
        libraDoc && docData
          ? findEntryId(citation.doc, docData.documentsByCategory)
          : null

      const handleClick = entryId
        ? () =>
            libraDoc!.setSelectedDocumentId(entryId, citation.page ?? undefined)
        : undefined

      return (
        <Tooltip title={tooltipContent}>
          <CitationTag
            icon={<FileTextOutlined />}
            color='blue'
            onClick={handleClick}
            style={{ cursor: handleClick ? 'pointer' : 'default' }}>
            {label}
          </CitationTag>
        </Tooltip>
      )
    }

    if (isApmCitation(citation)) {
      // Zone pre-fill responses may omit display context while retaining the source.
      const component =
        typeof citation.component === 'string'
          ? citation.component.trim()
          : null
      const field =
        typeof citation.field === 'string' ? citation.field.trim() : null
      const context = [
        component ? `APM component: ${component}` : null,
        field ? `Field: ${startCase(field)}` : null,
      ]
        .filter(Boolean)
        .join(' · ')
      const tooltipContent = `${
        context || 'Sourced from APM component data'
      }${citation.excerpt ? ` — "${citation.excerpt}"` : ''}`
      return (
        <Tooltip title={tooltipContent}>
          <CitationTag color='purple'>
            {component ? `APM - ${component}` : 'APM source'}
          </CitationTag>
        </Tooltip>
      )
    }
  }

  if (fieldValue.aiExtracted === false) {
    const searched = fieldValue.documentsSearched ?? []
    const tooltipContent =
      searched.length > 0
        ? `Searched: ${searched.join(', ')}`
        : 'No relevant content found in available documents'
    return (
      <Tooltip title={tooltipContent}>
        <CitationTag color='warning'>Not in documents</CitationTag>
      </Tooltip>
    )
  }

  return null
}
